import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MyMarker } from './MyMarker';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { MyRenderer } from './MyRenderer';
import { useDispatch, useSelector } from 'react-redux';
import { setMap, setShowResetView } from '../../store/Actions';
import { SelectedMarker } from './SelectedMarker';
import { getSelectedEventPanelWidth, getSideNavWidth } from './mapLayout';

function MarkerListComponent(props) {
    const map = useMap();
    const mapsLibrary = useMapsLibrary('maps');
    const markerLibrary = useMapsLibrary('marker');
    const placesLibrary = useMapsLibrary('places');
    const coreLibrary = useMapsLibrary('core');

    const selectedEvent = useSelector(state=>state.selectedEvent);
    const events = useSelector(state=>state.filteredEvents);
    const appWidth = useSelector(state=>state.appWidth);
    const sideNavOpen = useSelector(state=>state.sideNavOpen);
    const mapResetTrigger = useSelector(state=>state.mapResetTrigger);
    const showResetView = useSelector(state=>state.showResetView);
    const eventList = useMemo(() => props.eventsOverride || (events ? Object.values(events) : []), [events, props.eventsOverride]);
    const [markers, setMarkers] = useState({});
    const clusterer = useRef(null);
    const dispatch = useDispatch();
    const markersRef = useRef({});
    const selectedEventRef = useRef(selectedEvent);
    const showResetViewRef = useRef(showResetView);

    // Holds the bounds the map was last fit to, so we can return to it.
    const defaultBoundsRef = useRef(null);
    // True while we're programmatically moving the map, so the user-interaction
    // listeners don't think the user just panned/zoomed.
    const isResettingRef = useRef(false);

    useEffect(() => {
        markersRef.current = markers;
    }, [markers]);

    useEffect(() => {
        selectedEventRef.current = selectedEvent;
    }, [selectedEvent]);

    useEffect(() => {
        showResetViewRef.current = showResetView;
    }, [showResetView]);

    const markUserMovedMap = useCallback(() => {
        if (isResettingRef.current) {
            return;
        }
        if (defaultBoundsRef.current && !showResetViewRef.current) {
            dispatch(setShowResetView(true));
        }
    }, [dispatch]);

    // Reusable: fit map to the given bounds and clamp the resulting zoom.
    const fitToBounds = useCallback((bounds) => {
        const isPortrait = window.innerWidth < window.innerHeight;
        isResettingRef.current = true;
        map.fitBounds(bounds, isPortrait ? 40 : 100);
        coreLibrary.event.addListenerOnce(map, 'idle', () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            let minZoom = 2;
            if (w < h) minZoom = w * 2 >= h ? 1 : 2;
            // Phone-sized viewports look too sparse at the wider clamps, so
            // give them an extra notch of zoom-in.
            if (w < 500) minZoom += 0.25;
            if (map.getZoom() < minZoom) {
                map.setZoom(minZoom);
            }
            // Release the lock on the next tick so the setZoom-induced
            // zoom_changed event also gets ignored.
            setTimeout(() => { isResettingRef.current = false; }, 0);
        });
    }, [coreLibrary, map]);

    // Initialize MarkerClusterer
    useEffect(() => {
        if (!map || !mapsLibrary || !markerLibrary || !placesLibrary || !coreLibrary)
            return;
        dispatch(setMap(map, mapsLibrary, markerLibrary, placesLibrary, coreLibrary));
        const idleListener = coreLibrary.event.addListener(map, 'idle', markUserMovedMap);
        if (!clusterer.current){
            clusterer.current = new MarkerClusterer({
                map,
                renderer: new MyRenderer(markerLibrary)
            });
            const clusteredMarkers = {...markersRef.current};
            if (selectedEventRef.current) {
                delete clusteredMarkers[selectedEventRef.current.id];
            }
            clusterer.current.addMarkers(Object.values(clusteredMarkers));
        }
        return () => {
            idleListener.remove();
        };
    }, [map, markerLibrary, mapsLibrary, placesLibrary, coreLibrary, markUserMovedMap, dispatch]);

    useEffect(()=>{
        if(selectedEvent && map && coreLibrary){
            dispatch(setShowResetView(true));
            const leftOccupiedWidth = sideNavOpen ? getSideNavWidth(appWidth) : 0;
            const rightOccupiedWidth = getSelectedEventPanelWidth(appWidth, sideNavOpen) + 20;
            const availableMapWidth = appWidth - leftOccupiedWidth - rightOccupiedWidth;
            const targetPinX = leftOccupiedWidth + (availableMapWidth / 2);
            const centerOffsetX = (appWidth / 2) - targetPinX;

            isResettingRef.current = true;
            map.setCenter(selectedEvent.location.coordinates);
            map.setZoom(12);
            coreLibrary?.event.addListenerOnce(map, 'idle', () => {
                map.panBy(centerOffsetX, 0);
                coreLibrary.event.addListenerOnce(map, 'idle', () => {
                    setTimeout(() => { isResettingRef.current = false; }, 0);
                });
            });
        }
    }, [selectedEvent, map, coreLibrary, appWidth, sideNavOpen, dispatch]);

    useEffect(()=>{
        if(eventList.length && map && coreLibrary){
            let bounds = new coreLibrary.LatLngBounds();
            eventList.forEach(event=>bounds.extend(event.location.coordinates));
            defaultBoundsRef.current = bounds;
            dispatch(setShowResetView(false));
            fitToBounds(bounds);
        }
    }, [eventList, map, coreLibrary, dispatch, fitToBounds]);

    // Re-fit to the saved bounds when the recenter FAB is clicked.
    useEffect(()=>{
        if (mapResetTrigger > 0 && defaultBoundsRef.current && map && coreLibrary) {
            fitToBounds(defaultBoundsRef.current);
            dispatch(setShowResetView(false));
        }
    }, [mapResetTrigger, map, coreLibrary, dispatch, fitToBounds]);

    // Update markers
    useEffect(() => {
        clusterer.current?.clearMarkers();
        const newMarkers = {...markers};
        if(selectedEvent)
            delete newMarkers[selectedEvent.id]
        clusterer.current?.addMarkers(Object.values(newMarkers));
    }, [markers, selectedEvent]);

    const setMarkerRef = useCallback((marker, key) => {
        if (marker && markersRef.current[key]) return;
        if (!marker && !markersRef.current[key]) return;
        setMarkers(prev => {
            if (marker) {
                return {...prev, [key]: marker};
            } else {
                const newMarkers = {...prev};
                delete newMarkers[key];
                return newMarkers;
            }
        });
    }, []);

    
    return (
        <>
            {eventList.map(event =>
                <MyMarker
                    key={event.id}
                    event={event}
                    setMarkerRef={setMarkerRef}
                    onClick={props.onMarkerClick}
                />
            )}
            <SelectedMarker onClick={props.onMarkerClick}/>
        </>
    )
  }

export const MarkerList = memo(MarkerListComponent);
  
